import React, { useContext, useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import LeftSidePopupLayout from "../../../../component/popup/modal-layout";
import Style from "./style.module.css";
import { GlobalContext } from "../../../../context/Context";
import {
  FeildError,
  FeildWraper,
  FormArea,
  FormLabel,
  SelectSearch,
} from "../../../../component/form/form-layout";
import Select from "react-select";
import { Phone, Mail, MessageSquare, Calendar } from "lucide-react";
import XCloseIcon from "../../../../component/icons/x-close";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import moment from "moment";
import Icon from "../../../../component/icons/inner-pages-icons";
import { height } from "@mui/system";

const activityIcons = {
  call: "phone",
  phone: "phone",
  email: "mail",
  sms: "sms",
  consultation: "consultation",
  default: "default",

  note: "note",
  meeting: "calendar",
  "follow-up": "calendar",
  followup: "calendar",
  stage: "arrowRight",
  voicemail: "mic",
  chat: "sms",
  comment: "sms",
  demo: "demo",
  messasge: "sms",
  presentation: "presentation",
  assign: "assign",
  unassign: "unassign",
  file: "file",
  brief: "brief",
};

const activityColors = {
  call: "#3b82f6", // blue
  email: "#8b5cf6", // violet
  sms: "#06b6d4", // cyan
  consultation: "#f59e0b", // amber
  note: "#f59e0b", // amber
  meeting: "#10b981", // emerald
  stage: "#6366f1", // indigo
  // "follow-up": "#6366f1", // indigo
  "follow-up": "#06b6d4", // indigo
  voicemail: "#ef4444", // red

  chat: "#0ea5e9", // sky blue
  presentation: "#14b8a6", // teal
  demo: "#a855f7", // purple
  default: "#a855f7", // purple
  messasge: "#f43f5e", // rose

  // remaining icons (added)
  phone: "#3b82f6", // blue
  comment: "#0ea5e9", // sky
  followup: "#06b6d4", // indigo
  assign: "#22c55e", // green
  unassign: "#ef4444", // red
  file: "#eab308", // slate
  brief: "#eab308", // yellow
};

const FollowUpPopup = ({
  type: parentType = "",
  UsersList = [],
  onSubmit = () => {},
  onClose = () => {},
  onError = () => {},
  CustomerData = {},
  id = "",
  entitytype = "",
  entityname = "",
}) => {
  // console.log("CustomerData", CustomerData)

  const { state } = useContext(GlobalContext);
  const apiCall = useApiCall();

  const userOptions = UsersList?.map((agents) => ({
    value: agents.userid,
    label: agents.name,
  }));

  const typeOptions = [
    {
      value: "call",
      label: "Call",
      icon: Phone,
      bg: "#e8f5e9",
      color: "#388e3c",
    },
    {
      value: "meeting",
      label: "Meeting",
      icon: Calendar,
      bg: "#fee2e2",
      color: "#b91c1c",
    },
    {
      value: "email",
      label: "Email",
      icon: Mail,
      bg: "#F3E8FF",
      color: "#9333EA",
    },
    {
      value: "follow-up",
      label: "Follow-up",
      icon: MessageSquare,
      bg: "#E8F0FE",
      color: "#1A73E8",
    },
    {
      value: "demo",
      label: "Demo",
      icon: Calendar,
      bg: "#fff3e0",
      color: "#e65100",
    },
    {
      value: "chat",
      label: "Chat",
      icon: MessageSquare,
      bg: "#E0F7FA",
      color: "#00838F",
    },
    {
      value: "consultation",
      label: "Consultation",
      icon: Calendar,
      bg: "#F5F5DC",
      color: "#6D4C41",
    },
    {
      value: "presentation",
      label: "Presentation",
      icon: Calendar,
      bg: "#FCE7F3",
      color: "#BE185D",
    },
  ];
  const priorityOptions = [
    { value: "low", label: "Low", color: "#388e3c", Bgcolor: "#e8f5e9" },
    { value: "medium", label: "Medium", color: "#e65100", Bgcolor: "#fff3e0" },
    { value: "high", label: "High", color: "#b91c1c", Bgcolor: "#fee2e2" },
  ];

  const validationSchema = yup.object({
    type: yup.string().required("This field is required"),
    datetime: yup.string().required("This field is required"),
    priority: yup.string().required("This field is required"),
    assigned: yup.string().required("This Field is Required"),
    details: yup.string().required("This field is required"),
  });

  const formik = useFormik({
    initialValues: {
      type: CustomerData?.type || "",
      datetime: CustomerData
        ? moment(
            `${CustomerData.due_date} ${CustomerData.due_time}`,
            "DD-MMM-YYYY hh:mm A",
          ).format("YYYY-MM-DDTHH:mm")
        : "",
      priority: CustomerData?.priority || "low",
      assigned: CustomerData?.assignee?.userid || "",
      details: CustomerData?.note || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const [due_date, due_time] = values.datetime.split("T");

        const data = {
          type: values.type,
          priority: values.priority,
          assigned_to: values.assigned,
          note: values.details,
          due_date,
          due_time,
          entity_id: CustomerData?.entity_id || id,
          entity_type: CustomerData?.entity_type || entitytype,
          status: "pending",
          subject: formik.values.subject || "Activity",
        };

        const isEdit = !!CustomerData?.id;

        const apiRes = await apiCall({
          method: "post",
          url: isEdit
            ? `activities/update-activity/${CustomerData.id}`
            : "activities/add-activity",
          data,
        });

        formik.resetForm();
        onSubmit(apiRes?.message || "Request Submitted");
        onClose();
      } catch (error) {
        console.error(error);
        const res = error?.response?.data;
        if (res?.errors) {
          formik.setErrors(
            Object.fromEntries(
              Object.entries(res.errors).map(([key, value]) => [key, value[0]]),
            ),
          );
        }
        onError(res?.respMsg || res?.message || "Something went wrong");
      }
    },
  });

  useEffect(() => {
    if (parentType) {
      formik.setFieldValue("type", parentType);
    }
  }, [parentType]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <LeftSidePopupLayout
        popupStatus={true}
        className={`${Style.addMeModal} ${Style.leaveModal} !gap-0`}
      >
        {/* HEADER */}

        <div
          className={`${Style.modal_header} ${Style.addMeModal_head} flex justify-between items-center`}
        >
          <h4>Add Follow Up</h4>

          <div
            onClick={onClose}
            className={`${Style.close_btn} cursor-pointer`}
          >
            <XCloseIcon />
          </div>
        </div>

        {/* BODY */}

        <div className={Style.addMeModal_body}>
          <div className={Style.dcmb__card}>
            <div className={Style.dcmb__cardBody}>
              <div className={`${Style.dcmb__cardBody_formArea} pt-3`}>
                <FormArea>
                  {/* TYPE TAGS */}

                  <FeildWraper className="!gap-[5px]" width="100%">
                    <FormLabel
                      required
                      className="!text-[12px] !text-[rgb(92,84,102)] !font-[600]"
                    >
                      Type
                    </FormLabel>

                    <div className="flex gap-2 flex-wrap">
                      {typeOptions.map((item) => {
                        // const Icon = item.icon;
                        const isActive = formik.values.type === item.value;

                        return (
                          //                         <button
                          //                           key={item.value}
                          //                           type="button"
                          //                           onClick={() =>
                          //                             formik.setFieldValue("type", item.value)
                          //                           }
                          //                           className={`flex items-center gap-[5px] px-[14px] py-[6px] rounded-[20px] border text-[12px] transition
                          // ${isActive ? "font-[600]" : "bg-white border-[rgba(0,0,0,0.1)] text-[rgb(144,136,146)] font-[400]"}
                          // `}
                          //                           style={{
                          //                             backgroundColor: isActive ? item.bg : "",
                          //                             borderColor: isActive ? item.bg : "",
                          //                             color: isActive ? item.color : "",
                          //                           }}
                          //                         >
                          //                           <Icon size={12} />
                          //                           {item.label}
                          //                         </button>

                          <button
                            key={item}
                            onClick={() =>
                              formik.setFieldValue("type", item.value)
                            }
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              padding: "5px 12px",
                              borderRadius: 20,
                              border: isActive
                                ? `1px solid ${activityColors[item?.label?.toLocaleLowerCase() || "default"]}25`
                                : "1px solid rgba(0,0,0,0.1)",
                              background: isActive
                                ? activityColors[
                                    item?.label?.toLocaleLowerCase()
                                  ] + "08"
                                : "#fff",
                              color: isActive
                                ? activityColors[
                                    item?.label?.toLocaleLowerCase()
                                  ]
                                : "rgb(144,136,146)",
                              cursor: "pointer",
                              fontSize: 11,
                              fontWeight: 600,
                              fontFamily: "inherit",
                            }}
                          >
                            <Icon
                              name={
                                activityIcons[
                                  item?.label?.toLocaleLowerCase() || "default"
                                ]
                              }
                              size={12}
                              color={
                                isActive
                                  ? activityColors[
                                      item?.label?.toLocaleLowerCase() ||
                                        "default"
                                    ]
                                  : "rgb(144,136,146)"
                              }
                            />
                            {item?.label?.charAt(0).toUpperCase() +
                              item?.label?.slice(1)}
                          </button>
                        );
                      })}
                    </div>

                    {formik.touched.type && formik.errors.type && (
                      <FeildError className="!mt-0">
                        {formik.errors.type}
                      </FeildError>
                    )}
                  </FeildWraper>

                  {/* DATE TIME */}

                  <FeildWraper className="!gap-[5px]" width="50%">
                    <FormLabel
                      required
                      className="!text-[12px] !text-[rgb(92,84,102)] !font-[600]"
                    >
                      Date & Time
                    </FormLabel>

                    <input
                      type="datetime-local"
                      name="datetime"
                      min={new Date().toISOString().slice(0, 16)}
                      value={formik.values.datetime}
                      onChange={formik.handleChange}
                      className="!bg-[rgb(243,239,233)]  !text-[rgb(92,84,102)] !h-[25px] !rounded-[5px] !text-[12px]"
                      // text-[rgb(26,21,32)]
                    />

                    {formik.touched.datetime && formik.errors.datetime && (
                      <FeildError className="!mt-0">
                        {formik.errors.datetime}
                      </FeildError>
                    )}
                  </FeildWraper>

                  {/* SUMMARY */}

                  <FeildWraper className="!gap-[5px]" width="50%">
                    <FormLabel
                      required
                      className="!text-[12px] !text-[rgb(92,84,102)] !font-[600]"
                    >
                      Assigned
                    </FormLabel>

                    {/* <SelectSearch
                      name="assigned"
                      placeholder="- Select Assigned -"
                      onChange={(val) => {
                        formik?.setFieldValue("assigned", val);
                      }}
                      value={formik?.values.assigned}
                      options={userOptions}
                      onBlur={() =>
                        formik.setFieldTouched("assigned", true, true)
                      }
                      menuPosition="fixed"
                      backgroundColor={"#f3efe9"}
                      minHeight={`25px`}
                      // maxHeight={`25px`}
                      borderRadius={`5px`}
                      className="text-[rgb(92,84,102)] !text-[12px] !h-[25px] !max-h-[25px]"
                    /> */}

                    <select
                      name="assigned"
                      value={formik.values.assigned} // controlled value
                      className="!text-[rgb(92,84,102)] !bg-[#f3efe9] !text-[12px] !h-[25px] !max-h-[25px]"
                      onBlur={() =>
                        formik.setFieldTouched("assigned", true, true)
                      }
                      onChange={(e) =>
                        formik.setFieldValue("assigned", e.target.value)
                      }
                    >
                      <option
                        value=""
                        disabled
                        className="text-[rgb(92,84,102)]"
                      >
                        - Select Assigned -
                      </option>
                      {userOptions?.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>

                    {/* <Select
                      name="assigned"
                      className="text-sm"
                      classNamePrefix="react-select"
                      options={userOptions}
                      components={{
                        IndicatorSeparator: () => null,
                        LoadingIndicator: () => null,
                      }}
                      value={userOptions?.find(
                        (option) => option.value === formik.values.assigned,
                      )}
                      onChange={(selectedOption) => {
                        formik.setFieldValue(
                          "assigned",
                          selectedOption?.value || "",
                        );
                      }}
                      filterOption={(option, inputValue) =>
                        option.label
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      }
                      placeholder="- Select Assigned -"
                      isClearable
                      menuPlacement="auto"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: "25px",
                          maxHeight: "25px",
                          height: "25px",
                          borderRadius: "5px",
                          border: "1px solid #DFDFDF",
                          backgroundColor: state.isDisabled
                            ? "#f0f0f0"
                            : "#f3efe9",
                          boxShadow: "none",
                          "&:hover": { borderColor: "#DFDFDF" },
                          fontSize: "13px",
                          // lineHeight: "1.2",
                          fontWeight: 500,
                          color: "rgb(92,84,102)",
                          padding: "0 14px",
                          cursor: state.isDisabled ? "not-allowed" : "normal",
                        }),

                        valueContainer: (base) => ({
                          ...base,
                          height: "25px",
                          padding: "0 8px",
                          display: "flex",
                          alignItems: "center", // center text vertically
                        }),

                        input: (base) => ({
                          ...base,
                          margin: 0,
                          padding: 0,
                          fontSize: "12px",
                          // lineHeight: "1.2",
                          fontWeight: 500,
                          color: "#1E1E1E",
                          // height: "25px",
                          lineHeight: "25px",
                        }),
                        // input: (base) => ({
                        //   ...base,
                        //   margin: 0,
                        //   padding: 0,
                        //   height: "25px", // force same as control
                        //   minHeight: "25px", // override default min-height
                        //   lineHeight: "25px", // align text vertically
                        //   fontSize: "12px",
                        //   fontWeight: 500,
                        //   color: "#1E1E1E",
                        // }),
                        singleValue: (base) => ({
                          ...base,
                          fontSize: "12px",
                          // lineHeight: "1.2",
                          fontWeight: 500,
                          color: "#1E1E1E",
                          margin: 0,
                        }),
                        placeholder: (base) => ({
                          ...base,
                          fontSize: "12px",
                          // lineHeight: "1.2",
                          fontWeight: 500,
                          color: "#717680",
                          margin: 0,
                        }),
                        // placeholder: (base) => ({
                        //   ...base,
                        //   margin: 0,
                        //   top: "50%",
                        //   transform: "translateY(-25%)",
                        //   fontSize: "12px",
                        //   fontWeight: 500,
                        //   color: "#717680",
                        // }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 99999,
                        }),
                        menu: (base) => ({
                          ...base,
                          marginTop: "2px",
                          borderRadius: "5px",
                          border: "1px solid #DFDFDF",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }),
                        option: (base, state) => ({
                          ...base,
                          fontSize: "12px",
                          // lineHeight: "1.2",
                          fontWeight: 500,
                          color: state.isSelected ? "#FFF" : "#1E1E1E",
                          backgroundColor: state.isSelected
                            ? "#3b82f6"
                            : state.isFocused
                              ? "#F3F4F6"
                              : "#FFF",
                          padding: "4px 14px",
                          cursor: "pointer",
                        }),
                        inputContainer: (base) => ({
                          ...base,
                          margin: 0,
                          padding: 0,
                          height: 25,
                           maxHeight: 25,
                        }),

                        indicatorsContainer: (base) => ({
                          ...base,
                          height: 25,
                          minHeight: 25,
                          maxHeight: 25,
                        }),
                        indicatorContainer: (base) => ({
                          ...base,
                          padding: 0,
                          height: 25,
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          padding: 2,
                          height: 25,
                        }),
                        clearIndicator: (base) => ({
                          ...base,
                          padding: 2,
                          height: 25,
                        }), // fix cross icon
                      }}
                    /> */}

                    {formik.touched.assigned && formik.errors.assigned && (
                      <FeildError className="!mt-0">
                        {formik.errors.assigned}
                      </FeildError>
                    )}
                  </FeildWraper>

                  <FeildWraper className="!gap-[5px]" width="100%">
                    <FormLabel
                      required
                      className="!text-[12px] !text-[rgb(92,84,102)] !font-[600]"
                    >
                      Priority
                    </FormLabel>

                    <div className="flex gap-2 flex-wrap">
                      {priorityOptions.map((item) => {
                        const isActive = formik.values.priority === item.value;

                        return (
                          <button
                            key={item.value}
                            type="button"
                            onClick={() =>
                              formik.setFieldValue("priority", item.value)
                            }
                            className={`flex items-center gap-[5px] px-[14px] py-[6px] rounded-[20px] border text-[11px] transition
          ${
            isActive
              ? "text-white font-[600]"
              : "bg-white border-[rgba(0,0,0,0.1)] text-[rgb(144,136,146)] font-[400]"
          }`}
                            style={{
                              backgroundColor: isActive ? item.Bgcolor : "",
                              borderColor: isActive ? item.Bgcolor : "",
                              color: isActive ? item.color : "",
                            }}
                          >
                            {item.label}
                          </button>
                        );
                      })}
                    </div>

                    {formik.touched.priority && formik.errors.priority && (
                      <FeildError className="!mt-0">
                        {formik.errors.priority}
                      </FeildError>
                    )}
                  </FeildWraper>

                  {/* DETAILS */}

                  <FeildWraper className="!gap-[5px]" width="100%">
                    <FormLabel
                      required
                      className="!text-[12px] !text-[rgb(92,84,102)] !font-[600]"
                    >
                      Details
                    </FormLabel>

                    <textarea
                      rows={4}
                      name="details"
                      placeholder="Enter details"
                      value={formik.values.details}
                      onChange={formik.handleChange}
                      className="!bg-[rgb(243,239,233)] text-[rgb(26,21,32)] !rounded-[5px] !text-xs h-full min-h-32 max-h-52"
                    />

                    {formik.touched.details && formik.errors.details && (
                      <FeildError className="!mt-0">
                        {formik.errors.details}
                      </FeildError>
                    )}
                  </FeildWraper>
                </FormArea>
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}

        <div className={Style.dcmb__cardFoot}>
          <button
            type="button"
            className={Style.dcmb__cancel}
            onClick={onClose}
          >
            Discard
          </button>

          <button type="submit" className={Style.dcmb__submit}>
            Submit
          </button>
        </div>
      </LeftSidePopupLayout>
    </form>
  );
};

export default FollowUpPopup;
